import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { selectAuth } from "../../../../redux/features/auth/auth-slice";
import { EDeviceTypes } from "../../../../shared/oversight-core/enums/device-types";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import AddDeviceModal from "../add-device-modal/add-device-modal";
import SimplifiedDeviceEnergyCard, {
  ESimplifiedEnergyUsageType,
  ISimplifiedDevice,
} from "../simplified-device-energy-card/simplified-device-energy-card";

interface IProps {
  devices: ISimplifiedDevice[];
  setDevices: React.Dispatch<React.SetStateAction<ISimplifiedDevice[]>>;
  isHideAddDeviceButton?: boolean;
  disabled?: boolean;
}

const AddUsageInfo = (props: IProps) => {
  const { devices, setDevices, isHideAddDeviceButton, disabled }: IProps =
    props;

  const auth = useSelector(selectAuth);

  const [isShowAddDeviceModal, setIsShowAddDeviceModal] = useState(false);

  const onSimplifiedDeviceEnergyChange = (
    type: ESimplifiedEnergyUsageType,
    id: string,
    value: string
  ) => {
    if (type === ESimplifiedEnergyUsageType.AMOUNT) {
      setDevices((ps) => {
        const index = ps.findIndex((p) => p.id === id);
        ps[index].amount = value;
        return [...ps];
      });
      return;
    }

    if (type === ESimplifiedEnergyUsageType.WATTAGE) {
      setDevices((ps) => {
        const index = ps.findIndex((p) => p.id === id);
        ps[index].wattage = value;
        return [...ps];
      });
      return;
    }

    if (type === ESimplifiedEnergyUsageType.USAGE) {
      setDevices((ps) => {
        const index = ps.findIndex((p) => p.id === id);
        ps[index].usage = value;
        return [...ps];
      });
    }
  };

  const onUnitChange = (id: string, units: number) => {
    setDevices((ps) => {
      const index = ps.findIndex((p) => p.id === id);
      ps[index].units = units;
      return [...ps];
    });
  };

  const onMaxPowerChange = (id: string, maxPower: number) => {
    setDevices((ps) => {
      const index = ps.findIndex((p) => p.id === id);
      ps[index].maxPower = maxPower;
      return [...ps];
    });
  };

  const onAddDevice = (deviceType: EDeviceTypes, deviceName: string) => {
    setIsShowAddDeviceModal(false);
    setDevices((prevDevices) => {
      const newDevice: ISimplifiedDevice = {
        deviceType,
        deviceName,
        amount: "",
        wattage: "",
        usage: "",
        id: uuidv4(),
        units: 0,
        maxPower: 0,
      };
      return [...prevDevices, newDevice];
    });
  };

  const onDelete = (id: string) => {
    setDevices((prevDevices) => {
      const index = prevDevices.findIndex((p) => p.id === id);
      return [...prevDevices.filter((_, i) => i !== index)];
    });
  };

  return (
    <>
      <Row>
        <Col>
          {devices.map((device, index) => {
            const bgColor =
              index % 2 === 0 ? "estimation-container" : "bg-background-23";

            return (
              <SimplifiedDeviceEnergyCard
                key={device.id}
                device={device}
                bgColor={bgColor}
                onChange={onSimplifiedDeviceEnergyChange}
                onUnitChange={onUnitChange}
                onMaxPowerChange={onMaxPowerChange}
                showErrorMessage={false}
                onDelete={onDelete}
                disabled={disabled}
              />
            );
          })}
        </Col>
      </Row>
      {!isHideAddDeviceButton && (
        <Row className={`mt-3 ${!auth.token ? `px-4` : ``}`}>
          <Col className="col-auto">
            <AppButton
              text="Add Device"
              onClick={() => {
                setIsShowAddDeviceModal(true);
              }}
              className="px-4"
            />
          </Col>
        </Row>
      )}
      <AddDeviceModal
        title="Add Device"
        show={isShowAddDeviceModal}
        devices={devices}
        onClose={() => {
          setIsShowAddDeviceModal(false);
        }}
        onCancel={() => {
          setIsShowAddDeviceModal(false);
        }}
        onAddDevice={onAddDevice}
      />
    </>
  );
};

export default AddUsageInfo;
